import { useEffect, useMemo, useState } from 'react'
import { listAccountHeads, deleteAccountHead, getAcGroups } from '../api/acHeadApi'
import { appConfirm } from '../../../../../components/AppConfirmModal'
import { useExportJob } from '../../../../../hooks/useExportJob'

export function useAcHeadListScreen() {
  const { triggerExport, isExporting } = useExportJob({
    module: 'acheads',
    fileFormat: 'XLSX',
  })

  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [departmentFilter, setDepartmentFilter] = useState('All')
  const [placeFilter, setPlaceFilter] = useState('All')
  const [groupFilter, setGroupFilter] = useState('All')
  const [headTypeFilter, setHeadTypeFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('Active') // 'Active' | 'Archive' | 'All'
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [pageSize, setPageSize] = useState(25)
  const [currentPage, setCurrentPage] = useState(1)

  const [items, setItems] = useState([])
  const [groups, setGroups] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isMutating, setIsMutating] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [reloadTick, setReloadTick] = useState(0)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError('')

    getAcGroups()
      .then((groupList) => {
        if (!active) return
        setGroups(Array.isArray(groupList) ? groupList : [])
      })
      .catch((e) => console.error('Failed to load AcGroups', e))

    listAccountHeads()
      .then((headsData) => {
        if (!active) return
        setItems(Array.isArray(headsData) ? headsData : [])
      })
      .catch((err) => {
        if (!active) return
        setError(err?.message || 'Failed to load Account Heads')
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [reloadTick])

  const filteredItems = useMemo(() => {
    let result = items

    // Status Filter
    if (statusFilter === 'Active') {
      result = result.filter((h) => h.status === 'Active' || h.is_active === 1)
    } else if (statusFilter === 'Archive') {
      result = result.filter((h) => h.status === 'Archive' || h.status === 'Inactive' || h.is_active === 0)
    }

    // Group Filter
    if (groupFilter !== 'All') {
      result = result.filter((h) => String(h.group || '').toLowerCase() === groupFilter.toLowerCase())
    }

    // Place Filter
    if (placeFilter !== 'All') {
      result = result.filter((h) => String(h.place || '').toLowerCase() === placeFilter.toLowerCase())
    }

    // Head Type Filter
    if (headTypeFilter !== 'All') {
      const q = headTypeFilter.toLowerCase()
      result = result.filter((h) =>
        String(h.group || '').toLowerCase().includes(q) ||
        String(h.name || '').toLowerCase().includes(q)
      )
    }

    // Search query with search field
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      result = result.filter((h) => {
        if (searchField === 'Name') return String(h.name || '').toLowerCase().includes(q)
        if (searchField === 'Alias') return String(h.alias || '').toLowerCase().includes(q)
        if (searchField === 'Group') return String(h.group || '').toLowerCase().includes(q)
        if (searchField === 'Place') return String(h.place || '').toLowerCase().includes(q)
        return (
          String(h.name || '').toLowerCase().includes(q) ||
          String(h.alias || '').toLowerCase().includes(q) ||
          String(h.group || '').toLowerCase().includes(q) ||
          String(h.place || '').toLowerCase().includes(q)
        )
      })
    }

    return result
  }, [items, statusFilter, groupFilter, placeFilter, headTypeFilter, searchField, searchText])

  const totalEntries = filteredItems.length
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize))
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredItems.slice(start, start + pageSize)
  }, [filteredItems, currentPage, pageSize])

  const handleArchive = (id) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const nextStatus = item.status === 'Active' || item.is_active === 1 ? 'Archive' : 'Active'
          return { ...item, status: nextStatus, is_active: nextStatus === 'Active' ? 1 : 0 }
        }
        return item
      })
    )
  }

  const resetFilters = () => {
    setCompanyFilter('All')
    setBranchFilter('All')
    setDepartmentFilter('All')
    setPlaceFilter('All')
    setGroupFilter('All')
    setHeadTypeFilter('All')
    setStatusFilter('Active')
    setSearchField('Name')
    setSearchText('')
    setFromDate('')
    setToDate('')
    setCurrentPage(1)
  }

  async function handleDelete(id, name) {
    const confirmed = await appConfirm({
      title: 'Delete Account Head',
      message: `Delete Account Head "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return
    setIsMutating(true)
    setError('')
    try {
      await deleteAccountHead(id)
      setSuccess(`Account Head "${name}" deleted successfully.`)
      setReloadTick((t) => t + 1)
    } catch (err) {
      setError(err?.message || 'Failed to delete Account Head')
    } finally {
      setIsMutating(false)
    }
  }

  return {
    state: {
      items: paginatedItems,
      allFilteredCount: totalEntries,
      groups,
      isLoading,
      isMutating,
      error,
      success,
      companyFilter,
      branchFilter,
      departmentFilter,
      placeFilter,
      groupFilter,
      headTypeFilter,
      statusFilter,
      searchField,
      searchText,
      fromDate,
      toDate,
      pageSize,
      currentPage,
      totalPages,
      isExporting,
    },
    actions: {
      setCompanyFilter,
      setBranchFilter,
      setDepartmentFilter,
      setPlaceFilter,
      setGroupFilter: (grp) => {
        setGroupFilter(grp)
        setCurrentPage(1)
      },
      setHeadTypeFilter,
      setStatusFilter,
      setSearchField,
      setSearchText,
      setFromDate,
      setToDate,
      resetFilters,
      handleArchive,
      setPageSize: (size) => {
        setPageSize(Number(size))
        setCurrentPage(1)
      },
      setCurrentPage,
      handleDelete,
      refresh: () => setReloadTick((t) => t + 1),
      triggerExport,
    },
  }
}

